import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AlertCircle, Clock, CalendarDays, CheckCircle, List, LayoutGrid, Trello, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { complianceAPI } from '../lib/complianceAPI';
import ComplianceCalendar from '../components/compliance/ComplianceCalendar';
import ComplianceKanban from '../components/compliance/ComplianceKanban';
import RemindersPanel from '../components/compliance/RemindersPanel';
import Compliance from './Compliance';

const typeLabels = { BAS: 'BAS', IAS: 'IAS', PAYG: 'PAYG', FBT: 'FBT', TAX_RETURN: 'Tax Return', ITR_INDIVIDUAL: 'Individual tax return', ITR_COMPANY: 'Company tax return', ITR_TRUST: 'Trust tax return', ITR_PARTNERSHIP: 'Partnership tax return', ITR_SMSF: 'SMSF annual return', PAYG_INSTALMENTS: 'PAYG instalments', ASIC_REVIEW: 'ASIC annual review', PAYROLL_TAX: 'Payroll tax', STP_FINALISATION: 'STP finalisation', TPAR: 'TPAR' };
const typeLabel = (t) => typeLabels[t] || String(t || '').replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());

// Bucket cards (spec Module 2A) — due today / 7 / 14 / 30, overdue, completed.
const bucketCards = [
  { key: 'overdue', label: 'Overdue', icon: AlertCircle, color: 'bg-red-100 text-red-600', text: 'Immediate attention' },
  { key: 'due_today', label: 'Due Today', icon: Clock, color: 'bg-orange-100 text-orange-600', text: 'Today' },
  { key: 'due_7', label: 'Due in 7 Days', icon: Clock, color: 'bg-amber-100 text-amber-600', text: 'This week' },
  { key: 'due_14', label: 'Due in 14 Days', icon: CalendarDays, color: 'bg-yellow-100 text-yellow-600', text: 'Next 2 weeks' },
  { key: 'due_30', label: 'Due in 30 Days', icon: CalendarDays, color: 'bg-blue-100 text-blue-600', text: 'This month' },
  { key: 'completed', label: 'Completed', icon: CheckCircle, color: 'bg-green-100 text-green-600', text: 'Lodged' },
];

export default function ComplianceDashboard() {
  const [summary, setSummary] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('overview'); // 'overview' | 'list' | 'calendar' | 'kanban' | 'reminders'

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [sumRes, listRes] = await Promise.all([
        complianceAPI.dashboard(),
        complianceAPI.list(),
      ]);
      setSummary(sumRes.data);
      setDeadlines(listRes.data);
    } catch (error) {
      toast.error('Failed to load compliance dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-6"><div className="animate-pulse h-64 bg-muted rounded"></div></div>;
  }

  const buckets = summary?.buckets || {};

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="compliance-dashboard-title">
            Compliance Dashboard
          </h1>
          <a href="/tax-bas" className="text-sm text-primary underline" data-testid="compliance-tax-bas-link">Tax &amp; BAS workflow dashboard →</a>
          <a href="/compliance-automation" className="text-sm underline text-primary" data-testid="compliance-automation-link">Compliance Overview &amp; automation (BAS/IAS) →</a>
          <p className="text-muted-foreground mt-1">Obligations across the practice, bucketed by due date</p>
        </div>
        <div className="flex gap-2">
          <div className="flex border rounded-lg p-1">
            <Button variant={view === 'overview' ? 'default' : 'ghost'} size="sm" onClick={() => setView('overview')} className={view === 'overview' ? 'bg-primary hover:bg-primary/90' : ''}>
              <LayoutGrid className="w-4 h-4 mr-2" /> Overview
            </Button>
            <Button variant={view === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setView('list')} className={view === 'list' ? 'bg-primary hover:bg-primary/90' : ''} data-testid="view-list">
              <List className="w-4 h-4 mr-2" /> List
            </Button>
            <Button variant={view === 'calendar' ? 'default' : 'ghost'} size="sm" onClick={() => setView('calendar')} className={view === 'calendar' ? 'bg-primary hover:bg-primary/90' : ''}>
              <CalendarDays className="w-4 h-4 mr-2" /> Calendar
            </Button>
            <Button variant={view === 'kanban' ? 'default' : 'ghost'} size="sm" onClick={() => setView('kanban')} className={view === 'kanban' ? 'bg-primary hover:bg-primary/90' : ''}>
              <Trello className="w-4 h-4 mr-2" /> Kanban
            </Button>
            <Button variant={view === 'reminders' ? 'default' : 'ghost'} size="sm" onClick={() => setView('reminders')} className={view === 'reminders' ? 'bg-primary hover:bg-primary/90' : ''} data-testid="view-reminders">
              <Bell className="w-4 h-4 mr-2" /> Reminders
            </Button>
          </div>
        </div>
      </div>

      {view === 'overview' ? (
        <>
          {/* Bucket cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {bucketCards.map((b) => (
              <Card key={b.key} data-testid={`bucket-${b.key}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">{b.label}</p>
                      <p className="text-3xl font-bold text-foreground mt-1">{buckets[b.key] || 0}</p>
                      <p className="text-xs text-muted-foreground/70 mt-1">{b.text}</p>
                    </div>
                    <div className={`w-10 h-10 ${b.color} rounded-lg flex items-center justify-center`}>
                      <b.icon className="w-5 h-5" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* By type breakdown */}
          <Card>
            <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>By Obligation Type</CardTitle></CardHeader>
            <CardContent>
              {(summary?.by_type || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No obligations yet.</p>
              ) : (
                <div className="space-y-2">
                  {summary.by_type.map((t) => (
                    <div key={t.type} className="flex items-center justify-between text-sm border-b pb-2 last:border-0">
                      <span className="font-medium text-foreground/90">{typeLabel(t.type)}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-muted-foreground">{t.open} open</span>
                        {t.overdue > 0 && <Badge className="bg-red-100 text-red-700">{t.overdue} overdue</Badge>}
                        <span className="text-muted-foreground/70">{t.total} total</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : view === 'list' ? (
        /* Full deadline list — add, filter, view detail, mark complete (merged from /compliance) */
        <Compliance embedded />
      ) : view === 'calendar' ? (
        /* Professional Google-Calendar-style view with Day/Week/Month/Year */
        <ComplianceCalendar deadlines={deadlines} typeLabels={typeLabels} />
      ) : view === 'kanban' ? (
        /* Kanban board, grouped by due-date bucket */
        <ComplianceKanban deadlines={deadlines} typeLabels={typeLabels} />
      ) : (
        /* Reminder drafting + review-before-send (merged from the old /reminders page) */
        <RemindersPanel />
      )}
    </div>
  );
}
